/**
 * Estado y reglas del formulario de dirección (MVP-02B §3).
 *
 * Perfil y Checkout comparten este hook, así que validan igual, resuelven el
 * código postal igual y tratan los errores del servidor igual. Antes cada uno
 * tenía su propia copia y divergían en todo lo que importaba.
 */
import { useCallback, useMemo, useState } from "react";

import { DIRECCION_VACIA, normalizarCodigoPostal } from "./api.js";
import { useGeografia } from "./useGeografia.js";

const CP_INACTIVO = { kind: "idle", coincidencias: [] };

/** Mismas reglas que el serializer, para fallar antes de la ida y vuelta. */
export function validarDireccion(value) {
    const errores = {};

    if (!value.nombre_contacto?.trim()) {
        errores.nombre_contacto = "El nombre de contacto es obligatorio.";
    }
    const telefono = value.telefono?.trim() ?? "";
    if (!telefono) {
        errores.telefono = "El teléfono es obligatorio.";
    } else {
        const digitos = telefono.replace(/\D/g, "");
        if (digitos.length < 9 || digitos.length > 15) {
            errores.telefono = "Introduce un teléfono válido.";
        }
    }
    if (!value.direccion?.trim()) errores.direccion = "La dirección es obligatoria.";
    if (!/^\d{5}$/.test(value.codigo_postal ?? "")) {
        errores.codigo_postal = "El código postal debe tener 5 dígitos.";
    }
    if (!value.provincia) errores.provincia = "Selecciona una provincia.";
    if (!value.municipio) errores.municipio = "Selecciona un municipio.";

    return errores;
}

export function useDireccionForm(inicial = DIRECCION_VACIA) {
    const [value, setValue] = useState({ ...DIRECCION_VACIA, ...inicial });
    const [errors, setErrors] = useState({});
    const [cpEstado, setCpEstado] = useState(CP_INACTIVO);

    const geografia = useGeografia(value.provincia);
    const { ensureMunicipios, buscarCodigoPostal, codigosPostalesDe } = geografia;

    const limpiarError = useCallback((campo) => {
        setErrors((prev) => (prev[campo] ? { ...prev, [campo]: undefined } : prev));
    }, []);

    const setField = useCallback((campo, valor) => {
        setValue((prev) => ({ ...prev, [campo]: valor }));
        limpiarError(campo);
    }, [limpiarError]);

    /** Reemplaza el valor entero, p. ej. al cargar una dirección existente. */
    const reset = useCallback((siguiente) => {
        setValue({ ...DIRECCION_VACIA, ...siguiente });
        setErrors({});
        setCpEstado(CP_INACTIVO);
    }, []);

    /**
     * Aplica una coincidencia de CP esperando a que la lista de municipios de
     * su provincia esté cargada.
     *
     * El `await` es el arreglo: fijar el municipio antes de tener la lista
     * dejaba el campo pintado en blanco aunque el estado tuviera el id, y si el
     * usuario guardaba en esa ventana la localidad se enviaba vacía.
     */
    const aplicarCoincidencia = useCallback(async (coincidencia, codigoPostal) => {
        setValue((prev) => ({ ...prev, provincia: coincidencia.provincia_id }));
        await ensureMunicipios(coincidencia.provincia_id);
        setValue((prev) => ({
            ...prev,
            provincia: coincidencia.provincia_id,
            municipio: coincidencia.id,
            codigo_postal: codigoPostal,
        }));
        setErrors((prev) => ({
            ...prev,
            provincia: undefined,
            municipio: undefined,
            codigo_postal: undefined,
        }));
    }, [ensureMunicipios]);

    const onCodigoPostalChange = useCallback(async (raw) => {
        const codigoPostal = normalizarCodigoPostal(raw);
        setField("codigo_postal", codigoPostal);

        if (codigoPostal.length !== 5) {
            setCpEstado(CP_INACTIVO);
            return;
        }

        setCpEstado({ kind: "searching", coincidencias: [] });
        const resultado = await buscarCodigoPostal(codigoPostal);

        if (resultado.kind === "single") {
            await aplicarCoincidencia(resultado.coincidencia, codigoPostal);
            setCpEstado({ kind: "ok", coincidencias: resultado.coincidencias });
            return;
        }
        // Con varias coincidencias no se elige por el usuario: hay códigos
        // compartidos por decenas de municipios.
        setCpEstado({ ...resultado, codigoPostal });
    }, [aplicarCoincidencia, buscarCodigoPostal, setField]);

    /** Resuelve una ambigüedad de CP con la opción que elige el usuario. */
    const elegirCoincidencia = useCallback(async (coincidencia) => {
        await aplicarCoincidencia(coincidencia, cpEstado.codigoPostal ?? value.codigo_postal);
        setCpEstado({ kind: "ok", coincidencias: [coincidencia] });
    }, [aplicarCoincidencia, cpEstado.codigoPostal, value.codigo_postal]);

    const onProvinciaChange = useCallback((provinciaId) => {
        setValue((prev) => ({ ...prev, provincia: provinciaId, municipio: "" }));
        setErrors((prev) => ({ ...prev, provincia: undefined }));
        setCpEstado(CP_INACTIVO);
    }, []);

    /**
     * Al elegir municipio el CP solo se toca si el actual no le sirve: pisar un
     * código postal válido de otra localidad sin avisar es peor que dejarlo.
     */
    const onMunicipioChange = useCallback((municipio) => {
        if (!municipio) return;
        const codigos = codigosPostalesDe(municipio.id);
        const actualSirve = codigos.includes(value.codigo_postal);

        setValue((prev) => ({
            ...prev,
            municipio: municipio.id,
            codigo_postal: actualSirve
                ? prev.codigo_postal
                : codigos.length === 1
                  ? codigos[0]
                  : prev.codigo_postal,
        }));
        setErrors((prev) => ({ ...prev, municipio: undefined }));

        if (!actualSirve && codigos.length > 1) {
            setCpEstado({
                kind: "varios_cp",
                coincidencias: [],
                municipio: municipio.nombre,
                codigos,
            });
        } else {
            setCpEstado(CP_INACTIVO);
        }
    }, [codigosPostalesDe, value.codigo_postal]);

    const validar = useCallback(() => {
        const encontrados = validarDireccion(value);
        setErrors(encontrados);
        return Object.keys(encontrados).length === 0;
    }, [value]);

    /** Municipio seleccionado, resuelto contra la lista ya cargada. */
    const municipioSeleccionado = useMemo(() => {
        if (!value.municipio) return null;
        return (
            geografia.municipios.data.find((m) => Number(m.id) === Number(value.municipio)) ?? null
        );
    }, [geografia.municipios.data, value.municipio]);

    return {
        value,
        errors,
        setErrors,
        setField,
        setValue,
        reset,
        validar,
        cpEstado,
        onCodigoPostalChange,
        onProvinciaChange,
        onMunicipioChange,
        elegirCoincidencia,
        municipioSeleccionado,
        geografia,
    };
}
